import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { motion, AnimatePresence } from 'framer-motion';
import { Navigation, Home, Briefcase, MapPin, X, Route, Loader2, Sun, Moon } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons
const homeIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#10b981" width="32" height="32">
      <circle cx="12" cy="12" r="12" fill="#10b981"/>
      <path d="M12 3L4 9v12h5v-7h6v7h5V9l-8-6z" fill="white" transform="scale(0.6) translate(8,8)"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const workIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#3b82f6" width="32" height="32">
      <circle cx="12" cy="12" r="12" fill="#3b82f6"/>
      <path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm-6 0h-4V5h4v2z" fill="white" transform="scale(0.5) translate(12,12)"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const gymIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#8b5cf6" width="28" height="28">
      <circle cx="12" cy="12" r="12" fill="#8b5cf6"/>
      <text x="12" y="16" text-anchor="middle" fill="white" font-size="12" font-weight="bold">G</text>
    </svg>
  `),
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28],
});

const gymOnRouteIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#f59e0b" width="32" height="32">
      <circle cx="12" cy="12" r="12" fill="#f59e0b"/>
      <text x="12" y="16" text-anchor="middle" fill="white" font-size="12" font-weight="bold">G</text>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Custom cluster icon creator
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let diameter = 36;

  if (count >= 10 && count < 30) {
    size = 'medium';
    diameter = 44;
  } else if (count >= 30) {
    size = 'large';
    diameter = 52;
  }

  return L.divIcon({
    html: `<div class="cluster-icon cluster-${size}"><span>${count}</span></div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(diameter, diameter),
  });
};

// Component to fit map bounds
function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, bounds]);
  return null;
}

// Component to track tile loading state
function TileLoadingTracker({ onLoadingChange }) {
  const map = useMap();

  useEffect(() => {
    const handleLoading = () => onLoadingChange(true);
    const handleLoad = () => onLoadingChange(false);

    map.on('loading', handleLoading);
    map.on('load', handleLoad);

    // Also track tile layer events
    map.eachLayer((layer) => {
      if (layer._url) {
        layer.on('loading', handleLoading);
        layer.on('load', handleLoad);
      }
    });

    return () => {
      map.off('loading', handleLoading);
      map.off('load', handleLoad);
    };
  }, [map, onLoadingChange]);

  return null;
}

const MapView = ({
  facilities = [],
  userLocations = {},
  onClose,
  onFacilitySelect,
  onFindRoute
}) => {
  const [routeData, setRouteData] = useState(null);
  const [facilitiesOnRoute, setFacilitiesOnRoute] = useState([]);
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);
  const [mapCenter, setMapCenter] = useState([52.2297, 21.0122]); // Warsaw default
  const [mapBounds, setMapBounds] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isTilesLoading, setIsTilesLoading] = useState(true);

  // Map tile URLs - CartoDB (free, no API key required)
  const tileUrls = {
    light: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    dark: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
  };

  // Tile layer options for maximum performance
  const tileOptions = {
    maxZoom: 20,
    minZoom: 3,
    keepBuffer: 6, // Keep more tiles in memory
    updateWhenZooming: false, // Don't reload during zoom
    updateWhenIdle: false, // Load immediately while panning
    tileSize: 256,
    zoomOffset: 0,
    crossOrigin: true, // Enable CORS for faster caching
  };

  // Calculate bounds from facilities and user locations
  useEffect(() => {
    const points = [];

    if (userLocations.home?.lat) {
      points.push([userLocations.home.lat, userLocations.home.lon]);
    }
    if (userLocations.work?.lat) {
      points.push([userLocations.work.lat, userLocations.work.lon]);
    }

    facilities.forEach(f => {
      // Support both flat and nested location structure
      const lat = f.latitude || f.location?.latitude;
      const lon = f.longitude || f.location?.longitude;
      if (lat && lon) {
        points.push([lat, lon]);
      }
    });

    if (points.length > 0) {
      setMapBounds(points);
      // Set center to first point
      setMapCenter(points[0]);
    }
  }, [facilities, userLocations]);

  // Find route between home and work
  const handleFindRoute = async () => {
    if (!userLocations.home || !userLocations.work) {
      return;
    }

    setIsLoadingRoute(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_MCP_MIDDLEWARE_URL}/api/gym-route`,
        {
          startAddress: userLocations.home.address,
          endAddress: userLocations.work.address,
          maxDetour: 1000, // 1km max detour
          maxResults: 20
        }
      );

      setRouteData(response.data.route);
      setFacilitiesOnRoute(response.data.facilities || []);

      // Notify parent about route facilities
      if (onFindRoute) {
        onFindRoute(response.data);
      }
    } catch (error) {
      console.error('Route error:', error);
    } finally {
      setIsLoadingRoute(false);
    }
  };

  // Check if a facility is on the route
  const isFacilityOnRoute = (facilityId) => {
    return facilitiesOnRoute.some(f => f.id === facilityId);
  };

  // Convert GeoJSON coordinates to Leaflet format
  const getRouteCoordinates = () => {
    if (!routeData?.geometry?.coordinates) return [];
    // GeoJSON is [lon, lat], Leaflet needs [lat, lon]
    return routeData.geometry.coordinates.map(([lon, lat]) => [lat, lon]);
  };

  return (
    <motion.div
      className="map-view-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Header */}
      <div className="map-header">
        <div className="map-title">
          <MapPin size={24} />
          <h2>Mapa obiektów</h2>
        </div>
        <div className="map-actions">
          {userLocations.home && userLocations.work && (
            <motion.button
              className="route-btn"
              onClick={handleFindRoute}
              disabled={isLoadingRoute}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isLoadingRoute ? (
                <>
                  <Loader2 size={18} className="spin" />
                  <span>Szukam trasy...</span>
                </>
              ) : (
                <>
                  <Route size={18} />
                  <span>Pokaż trasę dom-praca</span>
                </>
              )}
            </motion.button>
          )}
          <button
            className="theme-toggle-btn"
            onClick={() => setIsDarkMode(!isDarkMode)}
            title={isDarkMode ? "Jasny motyw" : "Ciemny motyw"}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="map-legend">
        {userLocations.home && (
          <div className="legend-item">
            <Home size={16} color="#10b981" />
            <span>Dom</span>
          </div>
        )}
        {userLocations.work && (
          <div className="legend-item">
            <Briefcase size={16} color="#3b82f6" />
            <span>Praca</span>
          </div>
        )}
        <div className="legend-item">
          <div className="legend-marker gym" />
          <span>Siłownia</span>
        </div>
        {facilitiesOnRoute.length > 0 && (
          <div className="legend-item">
            <div className="legend-marker on-route" />
            <span>Po drodze ({facilitiesOnRoute.length})</span>
          </div>
        )}
      </div>

      {/* Route info */}
      {routeData && (
        <motion.div
          className="route-info"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="route-stats">
            <div className="stat">
              <Navigation size={16} />
              <span>{routeData.distance_km} km</span>
            </div>
            <div className="stat">
              <span>{routeData.duration_minutes} min</span>
            </div>
          </div>
          <div className="route-facilities-count">
            Znaleziono <strong>{facilitiesOnRoute.length}</strong> obiektów po drodze
          </div>
        </motion.div>
      )}

      {/* Map */}
      <div className="map-container">
        {/* Loading overlay */}
        <AnimatePresence>
          {isTilesLoading && (
            <motion.div
              className="map-loading-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="loading-shimmer">
                <div className="shimmer-bar"></div>
                <div className="shimmer-bar"></div>
                <div className="shimmer-bar"></div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <MapContainer
          center={mapCenter}
          zoom={12}
          style={{ height: '100%', width: '100%' }}
        >
          {/* CartoDB - free minimalist tiles with theme toggle */}
          <TileLayer
            key={isDarkMode ? 'dark' : 'light'}
            attribution='&copy; <a href="https://carto.com/">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url={isDarkMode ? tileUrls.dark : tileUrls.light}
            subdomains="abcd"
            {...tileOptions}
          />

          <TileLoadingTracker onLoadingChange={setIsTilesLoading} />
          {mapBounds && <FitBounds bounds={mapBounds} />}

          {/* Route polyline */}
          {routeData && (
            <Polyline
              positions={getRouteCoordinates()}
              color="#667eea"
              weight={4}
              opacity={0.8}
            />
          )}

          {/* Home marker */}
          {userLocations.home?.lat && (
            <Marker
              position={[userLocations.home.lat, userLocations.home.lon]}
              icon={homeIcon}
            >
              <Popup>
                <div className="marker-popup">
                  <strong>Dom</strong>
                  <p>{userLocations.home.address}</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Work marker */}
          {userLocations.work?.lat && (
            <Marker
              position={[userLocations.work.lat, userLocations.work.lon]}
              icon={workIcon}
            >
              <Popup>
                <div className="marker-popup">
                  <strong>Praca</strong>
                  <p>{userLocations.work.address}</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Facility markers with clustering */}
          <MarkerClusterGroup
            chunkedLoading
            iconCreateFunction={createClusterCustomIcon}
            maxClusterRadius={60}
            spiderfyOnMaxZoom={true}
            showCoverageOnHover={false}
            zoomToBoundsOnClick={true}
            animate={true}
            animateAddingMarkers={false}
          >
            {facilities.map((facility) => {
              // Support both flat and nested location structure
              const lat = facility.latitude || facility.location?.latitude;
              const lon = facility.longitude || facility.location?.longitude;
              if (!lat || !lon) return null;

              const onRoute = isFacilityOnRoute(facility.id);
              const routeFacility = facilitiesOnRoute.find(f => f.id === facility.id);
              const city = facility.city || facility.address?.city;
              const facilityType = facility.type || facility.facility_type;

              return (
                <Marker
                  key={facility.id}
                  position={[lat, lon]}
                  icon={onRoute ? gymOnRouteIcon : gymIcon}
                >
                  <Popup>
                    <div className="marker-popup facility-popup">
                      <strong>{facility.name}</strong>
                      <p className="facility-type">{facilityType}</p>
                      {city && <p className="facility-city">{city}</p>}
                      {onRoute && routeFacility && (
                        <p className="detour-info">
                          Odległość od trasy: {routeFacility.detour_meters}m
                        </p>
                      )}
                      {onFacilitySelect && (
                        <button
                          className="popup-btn"
                          onClick={() => onFacilitySelect(facility)}
                        >
                          Znajdź karnet
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MarkerClusterGroup>
        </MapContainer>
      </div>

      {/* Facilities on route list */}
      {facilitiesOnRoute.length > 0 && (
        <motion.div
          className="route-facilities-list"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <h3>Obiekty po drodze</h3>
          <div className="facilities-scroll">
            {facilitiesOnRoute.slice(0, 10).map((facility) => (
              <div
                key={facility.id}
                className="route-facility-item"
                onClick={() => onFacilitySelect && onFacilitySelect(facility)}
              >
                <div className="facility-info">
                  <span className="facility-name">{facility.name}</span>
                  <span className="facility-details">
                    {facility.type} • {facility.detour_meters}m od trasy
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <style jsx>{`
        .map-view-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          z-index: 1000;
          display: flex;
          flex-direction: column;
        }

        .map-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          background: white;
          border-bottom: 1px solid #e5e7eb;
          z-index: 10;
        }

        .map-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
        }

        .map-title h2 {
          margin: 0;
        }

        .map-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }

        .route-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .route-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .theme-toggle-btn {
          background: #f3f4f6;
          border: none;
          border-radius: 50%;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          color: #6b7280;
        }

        .theme-toggle-btn:hover {
          background: #e5e7eb;
          color: #374151;
        }

        .close-btn {
          background: #f3f4f6;
          border: none;
          border-radius: 50%;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: #e5e7eb;
        }

        .map-legend {
          display: flex;
          gap: 1.5rem;
          padding: 0.75rem 1.5rem;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          flex-wrap: wrap;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #4b5563;
        }

        .legend-marker {
          width: 16px;
          height: 16px;
          border-radius: 50%;
        }

        .legend-marker.gym {
          background: #8b5cf6;
        }

        .legend-marker.on-route {
          background: #f59e0b;
        }

        .route-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1.5rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .route-stats {
          display: flex;
          gap: 1.5rem;
        }

        .stat {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
        }

        .route-facilities-count {
          font-size: 0.875rem;
        }

        .map-container {
          flex: 1;
          position: relative;
        }

        /* Loading overlay */
        .map-loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(2px);
          z-index: 500;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        }

        .loading-shimmer {
          display: flex;
          gap: 8px;
        }

        .shimmer-bar {
          width: 8px;
          height: 32px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 4px;
          animation: shimmer-wave 1s ease-in-out infinite;
        }

        .shimmer-bar:nth-child(2) {
          animation-delay: 0.15s;
        }

        .shimmer-bar:nth-child(3) {
          animation-delay: 0.3s;
        }

        @keyframes shimmer-wave {
          0%, 100% {
            transform: scaleY(1);
            opacity: 0.5;
          }
          50% {
            transform: scaleY(1.8);
            opacity: 1;
          }
        }

        /* Cluster styles */
        :global(.custom-cluster-icon) {
          background: transparent !important;
          border: none !important;
        }

        :global(.cluster-icon) {
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          font-weight: 700;
          color: white;
          box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4);
          transition: transform 0.2s ease;
        }

        :global(.cluster-icon:hover) {
          transform: scale(1.1);
        }

        :global(.cluster-small) {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          width: 36px;
          height: 36px;
          font-size: 12px;
        }

        :global(.cluster-medium) {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          width: 44px;
          height: 44px;
          font-size: 14px;
        }

        :global(.cluster-large) {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          width: 52px;
          height: 52px;
          font-size: 16px;
        }

        :global(.marker-cluster-small),
        :global(.marker-cluster-medium),
        :global(.marker-cluster-large) {
          background: transparent !important;
        }

        :global(.marker-cluster div) {
          background: transparent !important;
        }

        .route-facilities-list {
          position: absolute;
          bottom: 1rem;
          right: 1rem;
          width: 320px;
          max-height: 300px;
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          overflow: hidden;
        }

        .route-facilities-list h3 {
          padding: 1rem;
          margin: 0;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          font-size: 0.9375rem;
        }

        .facilities-scroll {
          max-height: 220px;
          overflow-y: auto;
        }

        .route-facility-item {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #f3f4f6;
          cursor: pointer;
          transition: background 0.2s;
        }

        .route-facility-item:hover {
          background: #f9fafb;
        }

        .facility-name {
          display: block;
          font-weight: 600;
          color: #1f2937;
          font-size: 0.875rem;
        }

        .facility-details {
          display: block;
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }

        /* Popup styles */
        :global(.marker-popup) {
          min-width: 150px;
        }

        :global(.marker-popup strong) {
          display: block;
          font-size: 0.9375rem;
          color: #1f2937;
          margin-bottom: 0.25rem;
        }

        :global(.marker-popup p) {
          margin: 0.25rem 0;
          font-size: 0.8125rem;
          color: #6b7280;
        }

        :global(.facility-popup .facility-type) {
          color: #8b5cf6;
          font-weight: 500;
        }

        :global(.facility-popup .detour-info) {
          color: #f59e0b;
          font-weight: 600;
        }

        :global(.popup-btn) {
          margin-top: 0.5rem;
          padding: 0.5rem 1rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 0.8125rem;
          font-weight: 600;
          cursor: pointer;
          width: 100%;
        }

        :global(.popup-btn:hover) {
          opacity: 0.9;
        }
      `}</style>
    </motion.div>
  );
};

export default MapView;
